import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createCommunity } from '@/api/communities';
import { toast } from '@/components/core/v2/toaster';
import { paths } from '@/paths';
import { useNavigate } from 'react-router-dom';

interface FormData {
  name: string;
}

interface CreateCommunityDialogProps {
  open: boolean;
  onClose: () => void;
}

export function CreateCommunityDialog({ open, onClose }: CreateCommunityDialogProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: { name: '' },
  });

  const mutation = useMutation({
    mutationFn: (data: FormData) => createCommunity({ name: data.name }),
    onSuccess: (community) => {
      queryClient.invalidateQueries({ queryKey: ['communities'] });
      toast.success(`Community "${community.name}" created`);
      reset();
      onClose();
      // Navigate to the new community's detail page
      const id = community.uuid ?? community.ulid ?? String(community.id);
      navigate(paths.settings.account.communities.community(id));
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg ?? 'Failed to create community');
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>New Community</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Enter the community name to get started. You can fill in the address and contact
            details on the next page.
          </Typography>
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Community name is required' }}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.name}>
                <InputLabel>Community name</InputLabel>
                <OutlinedInput
                  {...field}
                  label="Community name"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSubmit((d) => mutation.mutate(d))();
                  }}
                />
                {errors.name && <FormHelperText>{errors.name.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={mutation.isPending}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={mutation.isPending}
          onClick={handleSubmit((d) => mutation.mutate(d))}
        >
          Create
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
